<template>
  <div class="pt-5">
    <div class="flex flex-col gap-6 relative z-20">
      <div class="flex flex-wrap" :class="index == 0 ? '' : 'pt-6'">
        <div
          v-if="isAdult"
          class="relative z-[200] px-2 w-full"
          :class="isAdult ? 'lg:w-2/3' : 'lg:w-1/3'"
        >
          <PhoneNumberInput
            name="phone_number"
            :phoneNumber="patient.phone_number"
            :country="patient.country"
            :errors="errors"
            :isRequired="true"
            @update="handlePhoneNumberInput($event)"
          />
        </div>
        <div
          class="relative z-0 px-2 w-full"
          :class="isAdult ? 'lg:w-1/3' : 'lg:w-1/3 order-2'"
        >
          <BaseInput
            name="pesel"
            label="Pesel"
            :value="patient.pesel"
            :disabled="patient.isNoPesel"
            :anyDirty="v$.$anyDirty"
            :errors="errors"
            :isRequired="!patient.isNoPesel"
            @input="updateTmpPatientValue('pesel', $event.target.value)"
          />

          <BaseCheckbox
            :id="`isNoPesel-${patient.id}`"
            name="pesel"
            :checked="patient.isNoPesel"
            :label="$t('auth.checkboxes.no_pesel_number')"
            :disabled="hasTypedPesel"
            @input="(checked) => updateTmpPatientValue('isNoPesel', checked)"
          />
        </div>

        <div
          v-show="patient.isNoPesel && isAdult"
          class="flex flex-wrap mt-3 w-full"
        >
          <div class="w-full lg:w-3/5 px-2">
            <BaseDatePicker
              :id="`patient-date-picker-${index}`"
              :value="patient.birth_date"
              name="birth_date"
              :label="$t('patients.enter_date_of_birth')"
              :anyDirty="v$.$anyDirty"
              :errors="errors"
              :isRequired="true"
              :max="maxBirthDate"
              @input="updateTmpPatientValue('birth_date', $event)"
            />
          </div>

          <div class="w-full lg:w-2/5 px-2">
            <BaseSelect
              :label="$t('auth.gender.title')"
              :options="sexOptions"
              :anyDirty="v$.$anyDirty"
              name="sex"
              :errors="errors"
              :initSelected="patient.sex"
              @selected="(value) => updateTmpPatientValue('sex', value)"
            />
          </div>
        </div>

        <div
          class="relative z-0 px-2 w-full"
          :class="isAdult ? 'lg:w-1/3' : 'lg:w-1/3'"
        >
          <BaseInput
            name="first_name"
            :value="patient.first_name"
            :label="$t('auth.first_name')"
            :anyDirty="v$.$anyDirty"
            :errors="errors"
            :isRequired="true"
            @input="updateTmpPatientValue('first_name', $event.target.value)"
          />
        </div>

        <div
          class="relative z-0 px-2 w-full"
          :class="isAdult ? 'lg:w-2/3' : 'lg:w-1/3'"
        >
          <BaseInput
            name="last_name"
            :value="patient.last_name"
            :label="$t('auth.last_name')"
            :anyDirty="v$.$anyDirty"
            :errors="errors"
            :isRequired="true"
            @input="updateTmpPatientValue('last_name', $event.target.value)"
          />
        </div>

        <div class="mt-4" v-if="canDelete">
          <button
            class="flex items-center space-x-2.5 text-sm text-primary-light font-semibold hover:opacity-70 cursor-pointer transition-ease-in-out shrink-0"
            @click="deleteTmpPatient(patient.id)"
          >
            <img
              src="@/assets/images/base/icons/close-light.svg"
              loading="lazy"
              class="shrink-0"
            /><span> {{ $t("patients.remove_patient") }}</span>
          </button>
        </div>
      </div>

      <div v-show="patient.isNoPesel && !isAdult" class="flex flex-wrap mt-3">
        <div class="w-full lg:w-3/5 px-2">
          <BaseDatePicker
            :id="`patient-date-picker-${index}`"
            :value="patient.birth_date"
            name="birth_date"
            :label="$t('patients.enter_date_of_birth')"
            :anyDirty="v$.$anyDirty"
            :errors="errors"
            :isRequired="true"
            :max="maxBirthDate"
            @input="updateTmpPatientValue('birth_date', $event)"
          />
        </div>

        <div class="w-full lg:w-2/5 px-2">
          <BaseSelect
            :label="$t('auth.gender.title')"
            :options="sexOptions"
            :anyDirty="v$.$anyDirty"
            name="sex"
            :errors="errors"
            :initSelected="patient.sex"
            @selected="(value) => updateTmpPatientValue('sex', value)"
          />
        </div>
      </div>
      <div v-if="patientIsInOutDataBase">
        <BaseAlert class="mb-4">{{
          $t("patients.patient_in_system")
        }}</BaseAlert>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf, or, and } from "@vuelidate/validators";
import BaseInput from "@/components/Core/BaseInput.vue";
import BaseCheckbox from "@/components/Core/BaseCheckbox.vue";
import BaseSelect from "@/components/Core/BaseSelect.vue";
import validationRules from "@/mixins/validationRules";
import PhoneNumberInput from "@/components/Core/PhoneNumberInput.vue";
import BaseDatePicker from "@/components/Core/BaseDatePicker.vue";
import BaseAlert from "@/components/Alerts/BaseAlert.vue";
import http from "@/utils/axios.js";

export default {
  name: "AddPatientForm",
  components: {
    BaseInput,
    BaseCheckbox,
    BaseSelect,
    PhoneNumberInput,
    BaseDatePicker,
    BaseAlert,
  },
  props: {
    index: {
      type: Number,
      required: true,
    },
    patient: {
      type: Object,
      required: true,
    },
    canDelete: {
      type: Boolean,
      required: true,
    },
  },
  mixins: [validationRules],
  data() {
    return {
      sexOptions: [
        { name: this.$t("auth.gender.f"), value: "f" },
        { name: this.$t("auth.gender.m"), value: "m" },
      ],
      isValidPhone: false,
      errors: {},
      patientIsInOutDataBase: false,
    };
  },
  computed: {
    isAdult() {
      return this.patient.age === "adult";
    },
    hasTypedPesel() {
      return this.patient.pesel.length > 0;
    },
    maxBirthDate() {
      const date = new Date();
      return date.setFullYear(date.getFullYear() - (this.isAdult ? 18 : 5));
    },
    ...mapGetters({
      tmpPatients: "checkout/getTmpPatients",
      patients: "checkout/getPatients",
    }),
  },
  methods: {
    async validate() {
      this.errors = {};
      let isValid = await this.v$.$validate();

      if (!isValid) {
        this.v$.$errors.forEach((error) => {
          this.errors = {
            ...this.errors,
            [error.$property]: `error.${error.$property}.${error.$validator}`,
          };
        });
      } else {
        this.errors = {};
      }

      if (this.isAdult && !this.isValidPhone) {
        this.errors = {
          ...this.errors,
          phone_number: "error.national_phone_number.required",
        };
        isValid = false;
      }

      return isValid;
    },
    updateTmpPatientValue(key, value) {
      this.updateTmpPatient({
        id: this.patient.id,
        patient: { ...this.patient, [key]: value },
      });

      if (this.v$.$anyDirty) {
        this.validate();
      }
    },
    handlePhoneNumberInput(event) {
      this.isValidPhone = event.isValid;
      if (event.countryCode != undefined) {
        this.updateTmpPatient({
          id: this.patient.id,
          patient: {
            ...this.patient,
            country: event.countryCode,
            country_code: event.countryCallingCode,
            phone_number: event.nationalNumber,
          },
        });
      }
      if (event.isValid && this.patient.phone_number.length === 9) {
        this.checkPhoneNumber(
          this.patient.phone_number,
          this.patient.country_code,
          event.countryCode
        );
      }
    },
    isDistinctPeselInPatients(value) {
      let quantity = 0;
      quantity += this.patients.filter(
        (patient) => patient.pesel == value
      ).length;
      quantity += this.tmpPatients.filter(
        (patient) => patient.pesel == value
      ).length;

      return quantity <= 1;
    },
    async checkPhoneNumber(phoneNumber, country_code, country) {
      this.errors = {};
      await http
        .post("/referrals/check-number", {
          national_number: phoneNumber,
          country_code: country_code,
          country: country,
        })
        .then(({ data }) => {
          this.errors = {};
          if (data) {
            this.patientIsInOutDataBase = true;
          } else {
            this.patientIsInOutDataBase = false;
          }
        });
    },
    ...mapMutations({
      updateTmpPatient: "checkout/updateTmpPatient",
      deleteTmpPatient: "checkout/deleteTmpPatient",
    }),
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      patient: {
        first_name: { required },
        last_name: { required },
        pesel: {
          required: requiredIf(() => !this.patient.isNoPesel),
          invalid: or(() => this.patient.isNoPesel, this.isValidPesel),
          invalid_date: or(
            () => this.patient.isNoPesel,
            and(
              () => this.patient.age === "kid",
              (value) => this.hasPeselAgeBettwen(value, 5, 18)
            ),
            and(
              () => this.patient.age === "adult",
              (value) => this.hasPeselAgeGreaterThan(value, 17)
            )
          ),
          distinct: or(
            () => this.patient.isNoPesel,
            (value) => this.isDistinctPeselInPatients(value)
          ),
        },
        sex: { required: requiredIf(() => this.patient.isNoPesel) },
        birth_date: {
          required: requiredIf(() => this.patient.isNoPesel),
          invalid_date: or(
            () => !this.patient.isNoPesel,
            and(
              () => this.patient.age === "kid",
              (value) => this.hasAgeBettwen(value, 5, 17)
            ),
            and(
              () => this.patient.age === "adult",
              (value) => this.hasAgeGreaterThan(value, 17)
            )
          ),
        },
      },
    };
  },
};
</script>
